import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { PercentageIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCountries } from '../../../../api/countries/get-countries'
import { getFormulas } from '../../../../api/formula/get-formulas'
import { getProducts } from '../../../../api/products/get-products'
import {
  ReductionTypeEnum,
  reductionTypeEnumMapping
} from '../../../../lib/types/enums/cse/reduction-type.enum'
import { CseReductionRuleInput } from '../../../../lib/types/models/cse/cse-reduction-rule'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ValidationButtons from '../../../elements/validation-buttons'

export const formSchema: ZodType<CseReductionRuleInput> = z
  .object({
    name: createStringSchema('Nom de la règle'),
    type: createEnumSchema({ fieldName: 'Type de réduction', enumType: ReductionTypeEnum }),
    reduction: createNumberSchema('Réduction', { isPositive: true }),
    journeyDuration: createNumberArraySchema('Durée', { isInteger: true }).default([]),
    countryIds: createNumberArraySchema('Pays', { isInteger: true }).default([]),
    formulaIds: createNumberArraySchema('Formules', { isInteger: true }).default([]),
    excludedProductIds: createNumberArraySchema('Produits exclus', {
      isInteger: true
    }).optional()
  })
  .superRefine((data, ctx) => {
    if (data.type === ReductionTypeEnum.PERCENTAGE) {
      if (data.reduction <= 0 || data.reduction > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'La réduction en pourcentage doit être comprise entre 1 et 100',
          path: ['reduction']
        })
      }
    }
  })

type Props = BaseFormProps<CseReductionRuleInput, true> & {
  className?: string
}

const CseReductionForm = ({ className, defaultValues, ...validationButtonProps }: Props) => {
  const form = useForm<CseReductionRuleInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  // Watch form values to trigger re-fetch when filters change
  const watchedCountryIds = form.watch('countryIds')
  const watchedFormulaIds = form.watch('formulaIds')
  const watchedJourneyDuration = form.watch('journeyDuration')

  const selectedCountries = countries?.data.filter((country) =>
    watchedCountryIds?.includes(country.id)
  )
  const selectedFormulas = formulas?.data.filter((formula) =>
    watchedFormulaIds?.includes(formula.id)
  )

  const { data: products } = useQuery({
    queryKey: [
      'products',
      selectedCountries?.map((c) => c.name),
      selectedFormulas?.map((f) => getTranslatedName(f)),
      watchedJourneyDuration
    ],
    queryFn: () =>
      getProducts({
        filter: {
          countries: selectedCountries?.map((country) => country.name),
          formulas: selectedFormulas?.map((formula) => getTranslatedName(formula)),
          durationInDays: watchedJourneyDuration
        },
        version: 'BASIC'
      }),
    placeholderData: keepPreviousData
  })

  // Filter out selected journeys that are no longer in the filtered results
  useEffect(() => {
    if (products?.data) {
      const currentExcludedIds = form.getValues('excludedProductIds')
      if (currentExcludedIds && currentExcludedIds.length > 0) {
        const availableProductIds = products.data.map((product) => product.id)
        const validExcludedIds = currentExcludedIds.filter((id) =>
          availableProductIds.includes(id)
        )

        // Only update if there's a difference to avoid unnecessary re-renders
        if (validExcludedIds.length !== currentExcludedIds.length) {
          form.setValue('excludedProductIds', validExcludedIds)
        }
      }
    }
  }, [products?.data, form])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex gap-4 flex-col', className)}>
          <FormField
            control={form.control}
            name={'name'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput required {...field} label={'Nom de la règle'} type={'text'} />
                </FormControl>
              </FormItem>
            )}
          />
          <div>
            <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
              Type
            </Typography>
            <div className="h-2" />
            <div className="flex items-center gap-8">
              {Object.values(ReductionTypeEnum).map((reductionType) => (
                <FormField
                  key={reductionType}
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          required
                          label={reductionTypeEnumMapping[reductionType]}
                          checked={field.value === reductionType}
                          onCheck={() => {
                            field.onChange(reductionType)
                            form.trigger('reduction')
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>
          <FormField
            control={form.control}
            name={'reduction'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    required
                    {...field}
                    icon={PercentageIcon}
                    label={'Réduction'}
                    type={'number'}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'countryIds'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    label={'Pays'}
                    dropDownPosition="above"
                    variant={'primary'}
                    options={
                      countries?.data.map((country) => ({
                        label: country.name,
                        value: country.id
                      })) ?? []
                    }
                    selectedOptions={
                      field.value?.map((countryId) => ({
                        label:
                          countries?.data.find((country) => country.id === countryId)?.name ?? '',
                        value: countryId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'formulaIds'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    label={'Formule(s)'}
                    variant={'primary'}
                    dropDownPosition="above"
                    options={
                      formulas?.data.map((formula) => ({
                        label: getTranslatedName(formula),
                        value: formula.id
                      })) ?? []
                    }
                    searchable
                    selectedOptions={
                      field.value?.map((formulaId) => ({
                        label: getTranslatedName(
                          formulas?.data.find((formula) => formula.id === formulaId)
                        ),
                        value: formulaId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'journeyDuration'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    type="number"
                    allowDecimals={false}
                    label="Durée (jours)"
                    variant="grey"
                    onChange={(value) => {
                      field.onChange(value.map((val) => Number(val)))
                    }}
                    selectedOptions={
                      field.value?.map((duration) => ({
                        label: duration.toString(),
                        value: duration
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'excludedProductIds'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    dropDownPosition="above"
                    disabled={!products?.data.length}
                    label={'Produits exclus'}
                    variant={'primary'}
                    options={
                      products?.data.map((product) => ({
                        label: product.name,
                        value: product.id
                      })) ?? []
                    }
                    searchable
                    selectedOptions={
                      field.value?.map((productId) => ({
                        label:
                          products?.data.find((product) => product.id === productId)?.name ?? '',
                        value: productId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default CseReductionForm
